// Feature: features/mph-sa-v2-comparison-route.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  VStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  SimpleGrid,
  Heading,
  HStack,
  Badge,
  Button,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';
import CaseHeaderPanel from './CaseHeaderPanel';
import Cms1500Replica from '../mph-sa/Cms1500Replica';

const DiagnosisTableCard = ({ results, highlightCodes = [], accentScheme = 'blue' }) => {
  if (!results) return null;
  const ordered = results.ordered_diagnoses || [];
  const retryInfo = results.broader_net_retry;

  return (
    <Card variant="outline" borderColor={accentScheme === 'purple' ? 'purple.200' : 'gray.200'}>
      <CardHeader pb={2} bg={accentScheme === 'purple' ? 'purple.50' : 'gray.50'}>
        <HStack justify="space-between" flexWrap="wrap">
          <Heading size="sm" color={accentScheme === 'purple' ? 'purple.700' : 'gray.700'}>
            Ordered Diagnoses ({ordered.length})
          </Heading>
          <HStack spacing={2}>
            {results.overall_confidence_score !== undefined && (
              <Badge
                colorScheme={results.overall_confidence_score >= 8 ? 'green' : 'red'}
                fontSize="xs"
              >
                Confidence: {results.overall_confidence_score}/10
              </Badge>
            )}
            {results.needs_review_score !== undefined && (
              <Tooltip label={results.needs_review_rationale || 'No rationale'} hasArrow>
                <Badge
                  colorScheme={results.needs_review_score < 30 ? 'green' : 'red'}
                  fontSize="xs"
                  cursor="help"
                >
                  Needs Review: {results.needs_review_score}
                </Badge>
              </Tooltip>
            )}
            {results.dx_review_required_score !== undefined && (
              <Tooltip label={results.dx_review_rationale || 'No rationale'} hasArrow>
                <Badge
                  colorScheme={results.dx_review_required_score < 70 ? 'teal' : 'orange'}
                  fontSize="xs"
                  cursor="help"
                >
                  DX Review: {results.dx_review_required_score}
                </Badge>
              </Tooltip>
            )}
            {retryInfo?.triggered && (
              <Tooltip
                label={`Pass 2 Broader-Net Retry (${retryInfo.trigger_reason || ''}) | Roots: ${(retryInfo.inferred_roots || []).join(', ')}`}
                hasArrow
              >
                <Badge colorScheme="purple" variant="solid" fontSize="xs" cursor="help">
                  Pass 2 Broader-Net Retry
                </Badge>
              </Tooltip>
            )}
          </HStack>
        </HStack>
      </CardHeader>
      <CardBody pt={2}>
        {results.overall_gut_check_notes && (
          <Alert
            status={results.overall_confidence_score < 8 ? 'warning' : 'info'}
            variant="subtle"
            borderRadius="md"
            mb={3}
            py={2}
            fontSize="xs"
          >
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">QA Gut Check Notes:</Text>
              <Text>{results.overall_gut_check_notes}</Text>
            </Box>
          </Alert>
        )}
        {ordered.length === 0 ? (
          <Text fontSize="sm" color="red.500" fontWeight="medium">
            No valid ICD-10 diagnoses in Box 21.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Ptr</Th>
                  <Th>Code</Th>
                  <Th>Description</Th>
                  <Th>Rationale / Evidence</Th>
                </Tr>
              </Thead>
              <Tbody>
                {ordered.map((dx, idx) => {
                  const isShared = highlightCodes.includes(dx.code);
                  return (
                    <Tr key={`${dx.code}-${idx}`} bg={isShared ? 'transparent' : 'purple.50'}>
                      <Td fontWeight="bold">{dx.pointer || String.fromCharCode(65 + idx)}</Td>
                      <Td fontWeight="bold" whiteSpace="nowrap">
                        <Badge colorScheme={isShared ? 'gray' : accentScheme}>
                          {dx.code}
                        </Badge>
                      </Td>
                      <Td fontSize="xs">{dx.description}</Td>
                      <Td fontSize="xs" color="gray.600">
                        {dx.rationale}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

const MPHSAV2Compare = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const claimType = 'standard';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [caseData, setCaseData] = useState(null);
  const [claimsData, setClaimsData] = useState(null);
  const [v1Results, setV1Results] = useState(null);
  const [v2Results, setV2Results] = useState(null);
  const [runningV1, setRunningV1] = useState(false);
  const [runningV2, setRunningV2] = useState(false);

  const [comparisonData, setComparisonData] = useState(null);
  const [generatingComparison, setGeneratingComparison] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!caseId || !accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        setCaseData(detailsData);
      } else {
        throw new Error('Failed to fetch case details');
      }

      const v1Response = await callApi(
        `/mph_sa/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=advanced`,
        accessToken,
        { method: 'GET' }
      );
      if (v1Response.ok) {
        const v1Data = await v1Response.json();
        if (v1Data.status === 'success') {
          setV1Results(v1Data.results);
          if (v1Data.claims) {
            setClaimsData(v1Data.claims);
          }
        }
      }

      const v2Response = await callApi(
        `/mph_sa/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=v2`,
        accessToken,
        { method: 'GET' }
      );
      if (v2Response.ok) {
        const v2Data = await v2Response.json();
        if (v2Data.status === 'success') {
          setV2Results(v2Data.results);
          if (v2Data.claims) {
            setClaimsData(v2Data.claims);
          }
        }
      }

      const compResponse = await callApi(
        `/mph_sa/v2/compare_runs/${caseId}?claim_type=${claimType}`,
        accessToken,
        { method: 'GET' }
      );
      if (compResponse.ok) {
        const compData = await compResponse.json();
        if (compData.status === 'success') {
          setComparisonData(compData);
        } else {
          setComparisonData(null);
        }
      }
    } catch (err) {
      console.error('Error fetching MPH SA comparison data:', err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  }, [caseId, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRunPipeline = async (version) => {
    const isV2 = version === 'v2';
    if (isV2) setRunningV2(true);
    else setRunningV1(true);
    setComparisonError(null);

    try {
      const allowNoop = caseData?.case_type === 'MPH Surgical Assist NoOp';
      const response = await callApi('/mph_sa/analyze_case', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          external_case_id: caseId,
          claim_type: claimType,
          pipeline_version: version,
          allow_noop: allowNoop,
          clean_phi: true,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.results) {
          if (isV2) setV2Results(data.results);
          else setV1Results(data.results);
        } else {
          setComparisonError(data.message || `Failed to run ${version.toUpperCase()} analysis.`);
        }
      } else {
        setComparisonError(`Server error while running ${version.toUpperCase()} analysis.`);
      }
    } catch (err) {
      setComparisonError(err.message || `Error running ${version.toUpperCase()} analysis.`);
    } finally {
      if (isV2) setRunningV2(false);
      else setRunningV1(false);
    }
  };

  const handleGenerateComparison = async () => {
    setGeneratingComparison(true);
    setComparisonError(null);
    try {
      const response = await callApi('/mph_sa/v2/compare_runs', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ external_case_id: caseId, claim_type: claimType }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setComparisonData(data);
        } else {
          setComparisonError(data.error_message || 'Failed to generate comparison.');
        }
      } else {
        setComparisonError('Server error while generating comparison.');
      }
    } catch (err) {
      setComparisonError(err.message || 'An error occurred.');
    } finally {
      setGeneratingComparison(false);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading MPH SA V1 vs V2 comparison data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const v1Codes = v1Results?.ordered_diagnoses?.map((d) => d.code) || [];
  const v2Codes = v2Results?.ordered_diagnoses?.map((d) => d.code) || [];
  const sharedCodes = v1Codes.filter((code) => v2Codes.includes(code));
  const v1PointerA = v1Codes[0] || 'None';
  const v2PointerA = v2Codes[0] || 'None';
  const pointerAChanged = v1PointerA !== v2PointerA;

  const llmAnalysis = comparisonData?.llm_analysis;
  const preferenceScore = llmAnalysis?.overall_preference_score ?? llmAnalysis?.v2_preference_score;
  const customerClaimCodes = comparisonData?.comparison_summary?.customer_claim_codes || [];

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />

      <Box maxW="1600px" mx="auto" p={4}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" align="center" flexWrap="wrap">
            <HStack spacing={4}>
              <Heading size="lg" color="gray.700">
                MPH Surgical Assist Pipeline Comparison (V1 vs V2)
              </Heading>
              <Button
                as={RouterLink}
                to={`/mph-surgical-assist/${caseId}`}
                size="sm"
                variant="outline"
                colorScheme="blue"
              >
                Back to SA Workbench
              </Button>
            </HStack>
            <Badge colorScheme="purple" p={2} borderRadius="md">
              V1 vs V2 A/B Testing (Standard Claim)
            </Badge>
          </HStack>

          {caseData && <CaseHeaderPanel caseData={caseData} />}

          {/* Sequence & Customer Claim Summary Strip */}
          <Box
            bg="white"
            p={4}
            borderRadius="lg"
            shadow="sm"
            border="1px solid"
            borderColor={pointerAChanged ? 'purple.300' : 'gray.200'}
          >
            <SimpleGrid columns={{ base: 1, md: customerClaimCodes.length > 0 ? 4 : 3 }} spacing={4} alignItems="center">
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  V1 (Current) Sequence
                </Text>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  {v1Codes.length > 0 ? (
                    v1Codes.map((c, idx) => (
                      <Badge key={`${c}-${idx}`} colorScheme={idx === 0 ? 'blue' : 'gray'} fontSize="sm" px={2} py={0.5}>
                        {String.fromCharCode(65 + idx)}: {c}
                      </Badge>
                    ))
                  ) : (
                    <Text fontSize="sm" color="red.400">
                      {v1Results ? '0 Diagnoses ([] Empty)' : 'Not Run'}
                    </Text>
                  )}
                </HStack>
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="bold" color="purple.600" textTransform="uppercase">
                  V2 (Index + Broader-Net) Sequence
                </Text>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  {v2Codes.length > 0 ? (
                    v2Codes.map((c, idx) => (
                      <Badge key={`${c}-${idx}`} colorScheme={idx === 0 ? 'purple' : 'gray'} fontSize="sm" px={2} py={0.5}>
                        {String.fromCharCode(65 + idx)}: {c}
                      </Badge>
                    ))
                  ) : (
                    <Text fontSize="sm" color="red.400">
                      {v2Results ? '0 Diagnoses ([] Empty)' : 'Not Run'}
                    </Text>
                  )}
                </HStack>
              </Box>

              {customerClaimCodes.length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase">
                    Customer Billed Claim DX
                  </Text>
                  <HStack spacing={2} mt={1} flexWrap="wrap">
                    {customerClaimCodes.map((c, idx) => (
                      <Badge key={`${c}-${idx}`} colorScheme="teal" fontSize="sm" px={2} py={0.5}>
                        {String.fromCharCode(65 + idx)}: {c}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}

              <Box textAlign={{ base: 'left', md: 'right' }}>
                {pointerAChanged ? (
                  <Badge colorScheme="green" fontSize="sm" p={2} borderRadius="md">
                    Pointer A: {v1PointerA} → {v2PointerA}
                  </Badge>
                ) : v1Codes.join(',') !== v2Codes.join(',') ? (
                  <Badge colorScheme="purple" fontSize="sm" p={2} borderRadius="md">
                    Secondary DX Updated
                  </Badge>
                ) : v1Results && v2Results ? (
                  <Badge colorScheme="gray" fontSize="sm" p={2} borderRadius="md">
                    Identical Sequence ({v1PointerA})
                  </Badge>
                ) : null}
              </Box>
            </SimpleGrid>
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
            {/* Left Column: V1 */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="gray.100" pb={2}>
                  <HStack justify="space-between">
                    <Box>
                      <Heading size="md" color="gray.600">
                        V1 (Current Production)
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        MPH_SURGICAL_ASSIST
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant={v1Results ? 'outline' : 'solid'}
                      onClick={() => handleRunPipeline('advanced')}
                      isLoading={runningV1}
                      loadingText="Running V1..."
                    >
                      {v1Results ? 'Re-Run V1' : 'Run V1 Analysis'}
                    </Button>
                  </HStack>
                </Box>

                {v1Results ? (
                  <>
                    <Cms1500Replica results={v1Results} claimsData={claimsData} claimType={claimType} />
                    <DiagnosisTableCard results={v1Results} highlightCodes={sharedCodes} accentScheme="blue" />
                  </>
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No V1 results found for this case. Click &quot;Run V1 Analysis&quot; to generate V1 output.
                  </Alert>
                )}
              </VStack>
            </Box>

            {/* Right Column: V2 */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="2px solid" borderColor="purple.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="purple.100" pb={2}>
                  <HStack justify="space-between">
                    <Box>
                      <Heading size="md" color="purple.700">
                        V2 (Alphabetical Index + Broader-Net Retry)
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        MPH_SURGICAL_ASSIST_V2
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant={v2Results ? 'outline' : 'solid'}
                      onClick={() => handleRunPipeline('v2')}
                      isLoading={runningV2}
                      loadingText="Running V2..."
                    >
                      {v2Results ? 'Re-Run V2' : 'Run V2 Analysis'}
                    </Button>
                  </HStack>
                </Box>

                {v2Results ? (
                  <>
                    <Cms1500Replica results={v2Results} claimsData={claimsData} claimType={claimType} />
                    <DiagnosisTableCard results={v2Results} highlightCodes={sharedCodes} accentScheme="purple" />
                  </>
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No V2 results found for this case yet. Click &quot;Run V2 Analysis&quot; above to generate V2 output.
                  </Alert>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* LLM Auditor Comparison Section */}
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200" mt={8}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Heading size="md" color="gray.700">
                  LLM Senior Auditor Comparison (V1 vs V2)
                </Heading>
                {!comparisonData && (
                  <Button
                    colorScheme="purple"
                    onClick={handleGenerateComparison}
                    isLoading={generatingComparison}
                    isDisabled={!v1Results || !v2Results}
                    loadingText="Analyzing..."
                  >
                    Generate LLM Comparison
                  </Button>
                )}
              </HStack>

              {comparisonError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {comparisonError}
                </Alert>
              )}

              {comparisonData && llmAnalysis && (
                <VStack align="stretch" spacing={6} mt={4}>
                  <HStack justify="space-between" p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                    <Box>
                      <Text fontWeight="bold" color="gray.600">
                        Winner
                      </Text>
                      <Badge
                        colorScheme={
                          llmAnalysis.winner === 'V2'
                            ? 'purple'
                            : llmAnalysis.winner === 'V1'
                            ? 'blue'
                            : 'gray'
                        }
                        fontSize="lg"
                        p={2}
                        mt={1}
                      >
                        {llmAnalysis.winner}
                      </Badge>
                    </Box>
                    <Box textAlign="right">
                      <Text fontWeight="bold" color="gray.600">
                        V2 Preference Score
                      </Text>
                      <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        color={preferenceScore >= 60 ? 'green.500' : preferenceScore <= 40 ? 'red.500' : 'gray.600'}
                      >
                        {preferenceScore} / 100
                      </Text>
                    </Box>
                  </HStack>

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Clinical Baseline
                    </Text>
                    <Text color="gray.700">{llmAnalysis.clinical_baseline}</Text>
                  </Box>

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Text fontWeight="bold" color="blue.600" mb={2}>
                        V1 Evaluation
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>
                        Defensibility:
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        {llmAnalysis.v1_evaluation?.defensibility_rationale}
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>
                        Medical Necessity &amp; Sequencing:
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        {llmAnalysis.v1_evaluation?.necessity_rationale}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" color="purple.600" mb={2}>
                        V2 Evaluation
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>
                        Defensibility:
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        {llmAnalysis.v2_evaluation?.defensibility_rationale}
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>
                        Medical Necessity &amp; Sequencing:
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        {llmAnalysis.v2_evaluation?.necessity_rationale}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Comparison Synthesis
                    </Text>
                    <Text color="gray.700">{llmAnalysis.comparison_synthesis}</Text>
                  </Box>

                  <HStack justify="flex-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateComparison}
                      isLoading={generatingComparison}
                    >
                      Regenerate
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default MPHSAV2Compare;
